import Link from '@pinecast/common/Link';
import {BlockSkeleton, InlineSkeleton} from '@pinecast/common/Skeleton';
import StatusMessage from '@pinecast/common/StatusMessage';
import {DashboardTitle} from '@pinecast/common/Text';
import type {QueryResult} from '@pinecast/gql';
import {useManagedMutation} from '@pinecast/gql';
import type {
  CreateTipSetupSession,
  CreateTipSetupSessionVariables,
} from '@pinecast/gql/__generated_tj__/CreateTipSetupSession';
import type {RecurringDonation_tipJarSubscription} from '@pinecast/gql/__generated_tj__/RecurringDonation';
import type {RecurringDonationSupplemental} from '@pinecast/gql/__generated_tj__/RecurringDonationSupplemental';
import {FormattedMessage} from '@pinecast/i18n';
import {useCSS} from '@pinecast/styles';
import gql from 'graphql-tag';
import * as React from 'react';

import {stripeKey, useStripe} from '../donationPage/useStripe';
import Amex from '../icons/Amex';
import Brandless from '../icons/Brandless';
import MasterCard from '../icons/MasterCard';
import Visa from '../icons/Visa';

export const CREATE_TIP_SETUP_SESSION = gql`
  mutation CreateTipSetupSession($slug: ID!) {
    createSetupCheckoutSession(podcastSlug: $slug) {
      sessionId
    }
  }
`;

// Stripe's `PaymentMethod.card.brand` values, with the name each brand goes
// by. A brand's name is the same in every language, so it is not a message.
// The icons are decorative: the name is always written out beside them.
const CARD_BRANDS = new Map<string, {icon?: React.ReactNode; name: string}>([
  ['amex', {icon: <Amex />, name: 'American Express'}],
  ['cartes_bancaires', {name: 'Cartes Bancaires'}],
  ['diners', {name: 'Diners Club'}],
  ['discover', {name: 'Discover'}],
  ['eftpos_au', {name: 'eftpos'}],
  ['jcb', {name: 'JCB'}],
  ['mastercard', {icon: <MasterCard />, name: 'Mastercard'}],
  ['unionpay', {name: 'UnionPay'}],
  ['visa', {icon: <Visa />, name: 'Visa'}],
]);

type Props = {
  subscription: RecurringDonation_tipJarSubscription;
  supplemental: QueryResult<RecurringDonationSupplemental>;
};

const RecurringDonationPaymentMethod = ({
  subscription,
  supplemental,
}: Props) => {
  const css = useCSS();
  const stripe = useStripe(stripeKey, subscription.podcast.tipJarAccountId!);

  const createTipSetupSession = useManagedMutation<
    CreateTipSetupSession,
    CreateTipSetupSessionVariables
  >(CREATE_TIP_SETUP_SESSION, {
    variables: {slug: subscription.podcast.slug},
  });
  const card = supplemental.data?.tipJarSubscription?.paymentCard;
  const brand = card && CARD_BRANDS.get(card.brand);

  // The session was made, but the browser could not get to Stripe's page.
  const [stripeFailed, setStripeFailed] = React.useState(false);

  const changePaymentMethod = async () => {
    setStripeFailed(false);
    const result = await createTipSetupSession.action();
    if (!result) {
      // The mutation holds the error, which is shown below.
      return;
    }
    const sessionId = result.createSetupCheckoutSession?.sessionId;
    let failed = true;
    try {
      // Null when Stripe.js could not load, which a content blocker can cause.
      const stripeJs = await stripe;
      if (sessionId && stripeJs) {
        // On success the browser leaves this page, so this only settles with
        // an error.
        failed = !!(await stripeJs.redirectToCheckout({sessionId})).error;
      }
    } catch {
      // `loadStripe` rejects when its script fails to load.
    }
    setStripeFailed(failed);
  };

  return (
    <>
      <DashboardTitle>
        <FormattedMessage
          description="Title of the payment method section"
          defaultMessage="Payment method"
        />
      </DashboardTitle>
      <div
        className={css({
          display: 'grid',
          gridTemplateColumns: '20px 1fr',
          gap: '12px',
        })}
      >
        {card ? (
          (brand?.icon ?? <Brandless />)
        ) : (
          <BlockSkeleton style={{width: 20, height: 20}} />
        )}
        <div className={css({display: 'flex', flexDirection: 'column'})}>
          <strong>
            {card && (
              <>
                {brand ? (
                  brand.name
                ) : (
                  <FormattedMessage
                    description="Shown in place of the brand of a payment card, such as Visa, when the brand is not known"
                    defaultMessage="Card"
                  />
                )}{' '}
              </>
            )}
            {'•••• '}
            {card ? card.last4 : <InlineSkeleton width="32px" />}
          </strong>
          <span>
            <FormattedMessage
              description="Label for credit card expiration date"
              defaultMessage="Expires"
              style={{marginRight: '4px'}}
            />
            {supplemental.data?.tipJarSubscription ? (
              supplemental.data.tipJarSubscription.paymentCard.expMonth
            ) : (
              <InlineSkeleton width="24px" />
            )}
            {' / '}
            {supplemental.data?.tipJarSubscription ? (
              supplemental.data.tipJarSubscription.paymentCard.expYear
            ) : (
              <InlineSkeleton width="32px" />
            )}
            {' · '}
            <Link onClick={changePaymentMethod}>
              <FormattedMessage
                description="Button to change the payment method for this subscription"
                defaultMessage="Change payment method"
              />
            </Link>
          </span>
          {/* Not `GQLButtonLabel`: the link sits in a line of text, and there
              is nothing to call saved, since success leaves for Stripe. */}
          <StatusMessage
            message={
              createTipSetupSession.error ||
              (stripeFailed ? (
                <FormattedMessage
                  description="Error shown when the Stripe page to change a subscription's payment card could not be opened"
                  defaultMessage="We couldn't open the payment page. Try again later."
                />
              ) : null)
            }
            pendingMessage={
              createTipSetupSession.pending ? (
                <FormattedMessage
                  description="Announced to screen reader users while the Stripe page to change a subscription's payment card is being prepared"
                  defaultMessage="Opening the payment page…"
                />
              ) : null
            }
            type="negative"
          />
        </div>
      </div>
    </>
  );
};

export default RecurringDonationPaymentMethod;
